// Dispositivos autorizados y sesión única.
//
// Objetivo: que marcar asistencia sólo se pueda desde los equipos de la
// empresa. Cada navegador genera un identificador propio la primera vez que
// abre la app; el admin le pone nombre y lo aprueba. Un equipo sin aprobar
// puede entrar y ver lo suyo, pero no marcar.
//
// Límite conocido: la comprobación vive en el cliente. Frena el uso casual del
// celular, que es el caso real, pero alguien con conocimientos técnicos podría
// saltarla. La versión a prueba de todo exige mover la marcación a una Cloud
// Function que valide dispositivo e IP antes de escribir, y cerrar las reglas
// de Firestore.
import {
    collection, doc, getDoc, setDoc, updateDoc, deleteDoc, onSnapshot,
    query, where, serverTimestamp, increment, arrayUnion,
} from 'firebase/firestore';
import { db } from './firebase';
import { getUserWorkspace } from './firestore';

const COLLECTION = 'trusted_devices';
const STORAGE_KEY = 'gestorpro_device_id';
const SECURITY_DOC = () => doc(db, 'app_config', 'security');

let _wsId = null;
export const setDeviceWorkspace = async (userId) => {
    if (!userId) return null;
    _wsId = await getUserWorkspace(userId);
    return _wsId;
};

// ── Identidad del navegador ────────────────────────────────────────────────
// Se guarda en localStorage. Si el usuario borra los datos del navegador o usa
// una ventana de incógnito, se genera otro y el equipo vuelve a estar pendiente
// de aprobación: es el precio de no instalar nada en las máquinas.
export const getDeviceId = () => {
    try {
        let id = localStorage.getItem(STORAGE_KEY);
        if (!id) {
            const rand = (typeof crypto !== 'undefined' && crypto.randomUUID)
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            id = `dev-${rand}`;
            localStorage.setItem(STORAGE_KEY, id);
        }
        return id;
    } catch {
        // Sin localStorage (modo restringido) no hay identidad estable.
        return null;
    }
};

// Nombre legible para que el admin reconozca el equipo en la lista.
export const describeDevice = () => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const so = /Windows NT 10/.test(ua) ? 'Windows'
        : /Windows/.test(ua) ? 'Windows'
            : /Mac OS X/.test(ua) ? 'Mac'
                : /Android/.test(ua) ? 'Android'
                    : /iPhone|iPad|iPod/.test(ua) ? 'iPhone/iPad'
                        : /Linux/.test(ua) ? 'Linux' : 'Desconocido';
    const nav = /Edg\//.test(ua) ? 'Edge'
        : /OPR\//.test(ua) ? 'Opera'
            : /Chrome\//.test(ua) ? 'Chrome'
                : /Firefox\//.test(ua) ? 'Firefox'
                    : /Safari\//.test(ua) ? 'Safari' : 'Navegador';
    const movil = /Android|iPhone|iPad|iPod|Mobile/.test(ua);
    return { so, nav, movil, userAgent: ua, label: `${so} · ${nav}` };
};

// ── Registro y consulta ────────────────────────────────────────────────────
// Deja constancia de que este equipo existe y quién lo está usando. No aprueba
// nada: los equipos nuevos nacen pendientes.
export const registerDevice = async (user) => {
    const deviceId = getDeviceId();
    if (!deviceId || !user?.uid) return null;
    const info = describeDevice();
    const ref = doc(db, COLLECTION, deviceId);
    try {
        const snap = await getDoc(ref);
        const visto = {
            lastSeenAt: serverTimestamp(),
            lastUserId: user.uid,
            lastUserName: user.displayName || user.email || '',
            lastUserEmail: user.email || '',
            userAgent: info.userAgent,
            so: info.so,
            navegador: info.nav,
            esMovil: info.movil,
            workspaceId: _wsId || 'default',
        };
        if (snap.exists()) {
            await updateDoc(ref, visto);
            return { id: deviceId, ...snap.data(), ...visto };
        }
        const nuevo = {
            deviceId,
            label: info.label,
            approved: false, // los equipos nuevos nacen pendientes a proposito
            createdAt: serverTimestamp(),
            ...visto,
        };
        await setDoc(ref, nuevo);
        return { id: deviceId, ...nuevo };
    } catch (error) {
        console.error('Error al registrar el dispositivo:', error);
        return null;
    }
};

export const subscribeToDevices = (callback) => {
    const colRef = collection(db, COLLECTION);
    const q = _wsId ? query(colRef, where('workspaceId', '==', _wsId)) : query(colRef);
    return onSnapshot(q, (snap) => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.lastSeenAt?.toMillis?.() || 0) - (a.lastSeenAt?.toMillis?.() || 0));
        callback(list);
    }, (error) => {
        console.error('Error al leer los dispositivos:', error);
        callback([]);
    });
};

export const approveDevice = (deviceId, label) =>
    updateDoc(doc(db, COLLECTION, deviceId), {
        approved: true,
        ...(label ? { label } : {}),
        approvedAt: serverTimestamp(),
    });

export const revokeDevice = (deviceId) =>
    updateDoc(doc(db, COLLECTION, deviceId), { approved: false, approvedAt: null });

export const renameDevice = (deviceId, label) =>
    updateDoc(doc(db, COLLECTION, deviceId), { label });

export const deleteDevice = (deviceId) => deleteDoc(doc(db, COLLECTION, deviceId));

// Cierra la sesión de alguien a distancia: al borrar el identificador, su
// navegador detecta el cambio y se desconecta.
export const closeUserSession = (userId) =>
    updateDoc(doc(db, 'users', userId), { activeSessionId: null });

// ── Ajustes de seguridad (app_config/security) ─────────────────────────────
// Viven fuera de overtime_global porque los lee tambien el login, que no
// pertenece al modulo de horas extra.
export const subscribeToSecuritySettings = (callback) =>
    onSnapshot(SECURITY_DOC(), (snap) => {
        callback(snap.exists() ? snap.data() : {});
    }, (error) => {
        console.error('Error al leer los ajustes de seguridad:', error);
        callback({});
    });

export const saveSecuritySettings = (settings) =>
    setDoc(SECURITY_DOC(), { ...settings, updatedAt: serverTimestamp() }, { merge: true });

// ── Registro de IPs ────────────────────────────────────────────────────────
// El navegador no puede saber su IP pública, asi que se la pregunta a la Cloud
// Function `whoami`, que la lee de la propia peticion. Se guarda agregada por
// IP: cuantas conexiones, desde que equipos y quienes. Con eso se ve en unos
// dias si la IP de la oficina se mantiene o va cambiando.
const WHOAMI_URL = 'https://us-central1-gestor-de-respuestas-hingenia.cloudfunctions.net/whoami';
const IP_COLLECTION = 'network_ips';

let _cachedIp = null;

export const getPublicIp = async (idToken) => {
    if (_cachedIp) return _cachedIp;
    if (!idToken) return null;
    try {
        const res = await fetch(WHOAMI_URL, { headers: { Authorization: `Bearer ${idToken}` } });
        if (!res.ok) return null;
        const data = await res.json();
        _cachedIp = data.ip || null;
        return _cachedIp;
    } catch (error) {
        // Sin conexion o funcion caida: no es critico, simplemente no se registra.
        console.error('No se pudo obtener la IP publica:', error);
        return null;
    }
};

// La IP no sirve como id de documento tal cual (los puntos y dos puntos dan
// problemas de lectura), asi que se sustituyen por guiones.
const ipDocId = (ip) => ip.replace(/[.:]/g, '-');

export const logIp = async (ip, user) => {
    if (!ip || !user?.uid) return;
    const info = describeDevice();
    const deviceId = getDeviceId();
    try {
        const ref = doc(db, IP_COLLECTION, ipDocId(ip));
        const snap = await getDoc(ref);
        const comun = {
            ip,
            workspaceId: _wsId || 'default',
            lastSeenAt: serverTimestamp(),
            lastUserName: user.displayName || user.email || '',
        };
        if (snap.exists()) {
            await updateDoc(ref, {
                ...comun,
                count: increment(1),
                deviceIds: arrayUnion(deviceId),
                deviceLabels: arrayUnion(info.label),
                userNames: arrayUnion(user.displayName || user.email || ''),
            });
        } else {
            await setDoc(ref, {
                ...comun,
                firstSeenAt: serverTimestamp(),
                count: 1,
                deviceIds: deviceId ? [deviceId] : [],
                deviceLabels: [info.label],
                userNames: [user.displayName || user.email || ''],
            });
        }
    } catch (error) {
        console.error('No se pudo registrar la IP:', error);
    }
};

export const subscribeToIps = (callback) => {
    const colRef = collection(db, IP_COLLECTION);
    const q = _wsId ? query(colRef, where('workspaceId', '==', _wsId)) : query(colRef);
    return onSnapshot(q, (snap) => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.lastSeenAt?.toMillis?.() || 0) - (a.lastSeenAt?.toMillis?.() || 0));
        callback(list);
    }, (error) => {
        console.error('Error al leer las IPs:', error);
        callback([]);
    });
};

// La IP ya resuelta en esta sesion, para adjuntarla a los marcajes sin esperar.
export const getCachedIp = () => _cachedIp;
